/**
 * Audio Manager
 * Centralized audio control with mixer settings
 */

import Phaser from 'phaser';
import audioConfigData from '@/config/audioConfig.json';

export interface MixerSettings {
  music: {
    volume: number;
    muted: boolean;
  };
  sfx: {
    volume: number;
    muted: boolean;
  };
}

export interface MusicConfig {
  key: string;
  path: string;
  loop: boolean;
}

/**
 * AudioManager - Handles all game audio with mixer controls
 */
export class AudioManager {
  private static instance: AudioManager;
  private scene?: Phaser.Scene;
  private currentMusic?: Phaser.Sound.BaseSound;
  private musicFadeTween?: Phaser.Tweens.Tween;
  private unlockCleanup?: () => void;
  private mixerSettings: MixerSettings;
  private musicConfigs: Record<string, MusicConfig>;

  private constructor() {
    // Load mixer settings from config
    this.mixerSettings = audioConfigData.mixer as MixerSettings;
    this.musicConfigs = audioConfigData.music as Record<string, MusicConfig>;
    
    // Load from localStorage if available
    this.loadSettings();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * Initialize with a Phaser scene
   */
  initialize(scene: Phaser.Scene): void {
    if (this.scene !== scene) {
      this.clearUnlockListeners();
    }
    this.scene = scene;
  }

  /**
   * Get all music configurations for loading
   */
  static getAllMusicConfigs(): MusicConfig[] {
    const configs = audioConfigData.music as Record<string, MusicConfig>;
    return Object.values(configs);
  }

  /**
   * Play music by name with fade-in effect
   */
  playMusic(musicName: string, fadeInDuration: number = 2000): void {
    if (!this.scene) {
      console.warn('AudioManager not initialized with scene');
      return;
    }

    const config = this.musicConfigs[musicName];
    if (!config) {
      console.warn(`Music "${musicName}" not found in config`);
      return;
    }

    // Stop current music if playing
    this.stopMusic();
    this.clearUnlockListeners();

    // Calculate final volume (mixer volume * not muted)
    const targetVolume = this.mixerSettings.music.muted ? 0 : this.mixerSettings.music.volume;

    // Create the sound once and either play immediately or wait for unlock.
    this.currentMusic = this.scene.sound.add(config.key, {
      loop: config.loop,
      volume: 0
    });

    if (!this.isSceneAudioReady(this.scene)) {
      console.log(`🎵 Music ready: ${musicName} (waiting for browser audio unlock)`);
      this.setupAutoplayUnlock(targetVolume, fadeInDuration);
      return;
    }

    // Try to play immediately when audio is already unlocked
    const playPromise = this.currentMusic.play();

    if (playPromise !== false) {
      // Music started successfully, fade in
      this.fadeInMusic(targetVolume, fadeInDuration);
      console.log(`🎵 Playing music: ${musicName} (fading in to ${targetVolume})`);
    } else {
      // Fallback: browser declined immediate playback even though it looked unlocked.
      console.log(`🎵 Music ready: ${musicName} (waiting for user interaction)`);
      this.setupAutoplayUnlock(targetVolume, fadeInDuration);
    }
  }

  /**
   * Fade in music to target volume
   */
  private fadeInMusic(targetVolume: number, duration: number): void {
    if (!this.currentMusic || !this.scene) return;

    const music = this.currentMusic as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound;

    // Drop any previous fade so two fades never fight over the same sound.
    this.killMusicFadeTween();
    this.musicFadeTween = this.scene.tweens.add({
      targets: music,
      volume: targetVolume,
      duration: duration,
      ease: 'Linear',
      onComplete: () => { this.musicFadeTween = undefined; },
    });
  }

  /**
   * Stop + remove the in-flight music fade tween. Destroying a WebAudioSound
   * nulls its internal gain node; if a volume tween is still running against it
   * the next tween step reads `null.gain` and throws *inside the Phaser game
   * loop*, which freezes the whole game (black screen). Always kill the tween
   * before stopping/destroying the sound.
   */
  private killMusicFadeTween(): void {
    if (this.musicFadeTween) {
      try { this.musicFadeTween.stop(); } catch {}
      try { this.musicFadeTween.remove(); } catch {}
      this.musicFadeTween = undefined;
    }
    if (this.currentMusic && this.scene) {
      try { this.scene.tweens.killTweensOf(this.currentMusic); } catch {}
    }
  }

  /**
   * Setup autoplay unlock on first user interaction
   */
  private setupAutoplayUnlock(targetVolume: number, fadeInDuration: number): void {
    if (!this.scene) return;

    const scene = this.scene;
    const keyboard = scene.input.keyboard;
    let handled = false;

    const unlockAudio = () => {
      if (handled) {
        return;
      }

      if (!this.isSceneAudioReady(scene)) {
        return;
      }

      handled = true;
      this.clearUnlockListeners();

      if (this.currentMusic && !this.currentMusic.isPlaying) {
        const started = this.currentMusic.play();
        if (started !== false) {
          this.fadeInMusic(targetVolume, fadeInDuration);
          console.log('🎵 Music unlocked and now playing');
        }
      }
    };

    scene.sound.once('unlocked', unlockAudio);
    scene.input.once('pointerdown', unlockAudio);
    keyboard?.once('keydown', unlockAudio);
    this.unlockCleanup = () => {
      scene.sound.off('unlocked', unlockAudio);
      scene.input.off('pointerdown', unlockAudio);
      keyboard?.off('keydown', unlockAudio);
      this.unlockCleanup = undefined;
    };
  }

  /**
   * Stop current music
   */
  stopMusic(): void {
    this.clearUnlockListeners();
    if (this.currentMusic) {
      // Kill the fade tween BEFORE destroy so it can't write to a freed sound.
      this.killMusicFadeTween();
      try { this.currentMusic.stop(); } catch {}
      try { this.currentMusic.destroy(); } catch {}
      this.currentMusic = undefined;
    }
  }

  private clearUnlockListeners(): void {
    this.unlockCleanup?.();
  }

  private isSceneAudioReady(scene: Phaser.Scene): boolean {
    const soundManager = scene.sound as Phaser.Sound.BaseSoundManager & { context?: AudioContext };
    const audioState = soundManager.context?.state;
    return !scene.sound.locked && audioState !== 'suspended' && audioState !== 'interrupted';
  }

  /**
   * Play SFX with mixer settings
   */
  playSFX(scene: Phaser.Scene, key: string, config?: Phaser.Types.Sound.SoundConfig): void {
    if (this.mixerSettings.sfx.muted || !this.isSceneAudioReady(scene)) {
      return; // Don't play if muted
    }

    const volume = (config?.volume ?? 1) * this.mixerSettings.sfx.volume;
    scene.sound.play(key, { ...config, volume });
  }

  /**
   * AAA: Play SFX with spatial positioning (pan left/right based on X position)
   * @param scene Phaser scene
   * @param key Sound key
   * @param worldX X position in game world (0 to 1920)
   * @param config Optional sound config
   */
  playSpatialSFX(scene: Phaser.Scene, key: string, worldX: number, config?: Phaser.Types.Sound.SoundConfig): void {
    if (this.mixerSettings.sfx.muted || !this.isSceneAudioReady(scene)) return;

    // Calculate pan (-1 = full left, 0 = center, 1 = full right)
    const gameWidth = scene.cameras.main.width || 1920;
    const pan = Phaser.Math.Clamp((worldX / gameWidth) * 2 - 1, -1, 1);

    // Calculate volume with distance fallback (louder in center, softer at edges)
    const centeredness = 1 - Math.abs(pan) * 0.15; // Up to 15% quieter at edges
    const volume = (config?.volume ?? 1) * this.mixerSettings.sfx.volume * centeredness;

    // Apply slight pitch randomization for variety (0.92 to 1.08)
    const detune = (Math.random() - 0.5) * 160; // cents

    scene.sound.play(key, { ...config, volume, pan, detune });
  }

  /**
   * Oceanic Overwatch: Play bubble pop.
   *
   * Each size now has its own unique source file (bubble_pop_large/medium/small/tiny)
   * that carries the size character internally (bronze anvil on large, crystalline
   * tick on tiny, etc). We no longer layer a pitched-down splash on top — that
   * sub-bass layer sounded like a burp bleeding into scene transitions. Kept:
   * spatial pan + ±60¢ variety jitter so cascades feel alive.
   */
  playBubblePop(scene: Phaser.Scene, key: string, worldX: number, _size: 'large' | 'medium' | 'small' | 'tiny', config?: Phaser.Types.Sound.SoundConfig): void {
    if (this.mixerSettings.sfx.muted || !this.isSceneAudioReady(scene)) return;

    const gameWidth = scene.cameras.main.width || 1920;
    const pan = Phaser.Math.Clamp((worldX / gameWidth) * 2 - 1, -1, 1);
    const centeredness = 1 - Math.abs(pan) * 0.15;
    const volume = (config?.volume ?? 1) * this.mixerSettings.sfx.volume * centeredness;
    const detune = (Math.random() - 0.5) * 120;

    try {
      scene.sound.play(key, { ...config, volume, pan, detune });
    } catch {}
  }

  /**
   * Oceanic Overwatch — Poseidon voice stinger pools.
   *
   * English VO via ElevenLabs Clyde voice. Random pick from the context's pool
   * so lines don't repeat. Context pools defined below.
   */
  private voicePools: Record<string, string[]> = {
    stage_clear:  ['voice_excellent', 'voice_victory_mine', 'voice_the_wave'],
    combo_mid:    ['voice_impressive', 'voice_crushing'],
    combo_mega:   ['voice_unleash_tide', 'voice_behold_wrath'],
    powerup:      ['voice_power', 'voice_by_trident'],
    boss_kill:    ['voice_sea_is_mine'],
    game_over:    ['voice_tide_turns'],
    wrath:        ['voice_feel_depths', 'voice_behold_wrath'],
  };

  private voiceCooldown: Record<string, number> = {};

  /**
   * Play a random voice stinger from a named pool.
   * Includes a per-pool cooldown so the VO doesn't spam.
   */
  playVoiceStinger(scene: Phaser.Scene, pool: keyof AudioManager['voicePools'], cooldownMs = 800): void {
    if (this.mixerSettings.sfx.muted || !this.isSceneAudioReady(scene)) return;
    const now = scene.time.now;
    if ((this.voiceCooldown[pool] ?? 0) > now) return;
    this.voiceCooldown[pool] = now + cooldownMs;

    const keys = this.voicePools[pool];
    if (!keys || keys.length === 0) return;

    // Prefer keys that exist in the cache; fall back silently if none loaded
    const available = keys.filter(k => scene.cache.audio.exists(k));
    if (available.length === 0) return;

    const key = available[Math.floor(Math.random() * available.length)];
    const volume = 0.85 * this.mixerSettings.sfx.volume;
    try {
      scene.sound.play(key, { volume });
    } catch {}
  }

  /**
   * AAA: Play combo escalation sound (ascending pitch with combo count)
   * @param scene Phaser scene
   * @param comboCount Current combo count (1-8+)
   */
  playComboSFX(scene: Phaser.Scene, comboCount: number): void {
    if (this.mixerSettings.sfx.muted || !this.isSceneAudioReady(scene)) return;

    // Ascending pitch: each combo step raises pitch by ~150 cents (subtle escalation)
    const pitchIncrease = Math.min(comboCount - 1, 7) * 150;

    // Volume increases slightly with combo
    const volume = Math.min(0.3 + comboCount * 0.06, 0.7) * this.mixerSettings.sfx.volume;

    // Use the dedicated combo SFX — clean chime, not a bubble sound
    // Prefer sfx_combo (crisp hit), fall back to score_tick
    const comboKey = scene.cache.audio.exists('sfx_combo') ? 'sfx_combo'
      : (scene.cache.audio.exists('sfx_score_tick') ? 'sfx_score_tick' : null);

    if (comboKey) {
      try {
        scene.sound.play(comboKey, { volume, detune: pitchIncrease });
      } catch {
        // Fallback silently
      }
    }
  }

  /**
   * Set music volume (0.0 to 1.0)
   */
  setMusicVolume(volume: number): void {
    this.mixerSettings.music.volume = Phaser.Math.Clamp(volume, 0, 1);
    
    // Update current music if playing
    if (this.currentMusic && !this.mixerSettings.music.muted) {
      (this.currentMusic as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound).setVolume(this.mixerSettings.music.volume);
    }
    
    this.saveSettings();
  }

  /**
   * Set SFX volume (0.0 to 1.0)
   */
  setSFXVolume(volume: number): void {
    this.mixerSettings.sfx.volume = Phaser.Math.Clamp(volume, 0, 1);
    this.saveSettings();
  }

  /**
   * Toggle music mute
   */
  toggleMusicMute(): boolean {
    this.mixerSettings.music.muted = !this.mixerSettings.music.muted;
    
    // Update current music
    if (this.currentMusic) {
      const volume = this.mixerSettings.music.muted ? 0 : this.mixerSettings.music.volume;
      (this.currentMusic as Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound).setVolume(volume);
    }
    
    this.saveSettings();
    return this.mixerSettings.music.muted;
  }

  /**
   * Toggle SFX mute
   */
  toggleSFXMute(): boolean {
    this.mixerSettings.sfx.muted = !this.mixerSettings.sfx.muted;
    this.saveSettings();
    return this.mixerSettings.sfx.muted;
  }

  /**
   * Get current mixer settings
   */
  getMixerSettings(): MixerSettings {
    return { ...this.mixerSettings };
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem('audioSettings', JSON.stringify(this.mixerSettings));
    } catch (error) {
      console.warn('Failed to save audio settings:', error);
    }
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('audioSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.mixerSettings = { ...this.mixerSettings, ...parsed };
        console.log('📻 Loaded audio settings from localStorage');
      }
    } catch (error) {
      console.warn('Failed to load audio settings:', error);
    }
  }
}
